import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { galleryImages, staffMembers } from '../mock';
import { Button } from '../components/ui/button';

// Sadie's photo album
const sadiePhotos = [
  {
    url: "https://customer-assets.emergentagent.com/job_88aa9ec7-4e33-45f5-9f03-fcaf81350df6/artifacts/gxtl9vt5_IMG_3625.jpeg",
    alt: "Sadie with purple collar"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_88aa9ec7-4e33-45f5-9f03-fcaf81350df6/artifacts/lf5qcw69_IMG_2767.jpeg",
    alt: "Sadie sitting on deck"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_88aa9ec7-4e33-45f5-9f03-fcaf81350df6/artifacts/fymegwj5_IMG_9415.jpeg",
    alt: "Sadie resting on cozy blanket"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_88aa9ec7-4e33-45f5-9f03-fcaf81350df6/artifacts/ls3y9j3x_IMG_3483.jpeg",
    alt: "Sadie with her pack"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_88aa9ec7-4e33-45f5-9f03-fcaf81350df6/artifacts/qdzfnm88_IMG_3558.jpeg",
    alt: "Sadie in her senior years"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_dog-boarding-site/artifacts/nxwvngr6_IMG_2418.jpeg",
    alt: "Sadie relaxing"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_dog-boarding-site/artifacts/1g2tutae_IMG_3709.jpeg",
    alt: "Sadie enjoying the outdoors"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_dog-boarding-site/artifacts/yo1e12me_IMG_3182.jpeg",
    alt: "Sadie portrait"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_dog-boarding-site/artifacts/f75l1nwr_IMG_7414.jpeg",
    alt: "Sadie happy moment"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_dog-boarding-site/artifacts/2m34mcht_IMG_4695.jpeg",
    alt: "Sadie family time"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_dog-boarding-site/artifacts/0z4cpvrz_IMG_0085.jpeg",
    alt: "Sadie adventure"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_dog-boarding-site/artifacts/qqmuybse_19520C13-0FB4-4BC6-9499-7C18007D81CF.jpeg",
    alt: "Sadie sweet moment"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_dog-boarding-site/artifacts/wmr59nlj_IMG_6134.jpeg",
    alt: "Sadie cherished memory"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_dog-boarding-site/artifacts/m21134oh_IMG_3566.jpeg",
    alt: "Sadie peaceful"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_dog-boarding-site/artifacts/prv40828_IMG_3654.jpeg",
    alt: "Sadie lovely"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_dog-boarding-site/artifacts/986yopuh_IMG_3632.jpeg",
    alt: "Sadie precious"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_dog-boarding-site/artifacts/aw55t9sr_IMG_3634.jpeg",
    alt: "Sadie beloved"
  }
];

const About = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  const openLightbox = (index) => {
    setCurrentPhotoIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev + 1) % sadiePhotos.length);
  };

  const prevPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev - 1 + sadiePhotos.length) % sadiePhotos.length);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={closeLightbox}>
          <button 
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-50"
          >
            <X className="h-8 w-8" />
          </button>
          
          <button 
            onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
            className="absolute left-4 text-white hover:text-gray-300 z-50"
          >
            <ChevronLeft className="h-12 w-12" />
          </button>
          
          <img
            src={sadiePhotos[currentPhotoIndex].url}
            alt={sadiePhotos[currentPhotoIndex].alt}
            className="max-h-[85vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          
          <button 
            onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
            className="absolute right-4 text-white hover:text-gray-300 z-50"
          >
            <ChevronRight className="h-12 w-12" />
          </button>
          
          <div className="absolute bottom-4 text-white text-center">
            <p className="text-sm">{sadiePhotos[currentPhotoIndex].alt}</p>
            <p className="text-xs text-gray-400 mt-1">{currentPhotoIndex + 1} of {sadiePhotos.length}</p>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary/10 via-background to-secondary/30 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              About Sadie's Mission
            </h1>
            <p className="text-xl text-muted-foreground">
              A family-owned dog boarding facility dedicated to providing exceptional care
            </p>
          </div>
        </div>
      </section>

      {/* Sadie's Legacy Section */}
      <section className="section-padding">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">
              Sadie's Legacy
            </h2>
            
            <div className="space-y-6 text-muted-foreground">
              <p className="text-lg font-medium text-foreground">
                Sadie's Mission was built from love — and from one extraordinary dog.
              </p>
              
              <p>
                Jen and her husband rescued Sadie from a kill shelter when she had less than 24 hours left. They knew she was meant to be part of their family. A week later, she was home — anxious, fearful, and unsure of the world around her.
              </p>
              
              <p>
                Sadie struggled in the beginning. Loud noises and new experiences overwhelmed her. But beneath the anxiety was a deeply sensitive, loyal soul. With patience, structure, and unconditional love, she began to feel safe.
              </p>
              
              <p className="text-lg font-semibold text-foreground italic">
                And when she felt safe — she thrived.
              </p>
              
              <p>
                Sadie preferred humans over other dogs and loved simply being near her people. She basked in the sun, explored nature, and had an incredible emotional awareness — always knowing when someone needed comfort. She taught everyone around her a powerful truth: you cannot judge a book by its cover. Pitbull-type breeds — and many misunderstood dogs — simply need understanding.
              </p>
              
              <p>
                Later in life, Sadie bravely battled multiple malignant tumors, enduring several surgeries, including the removal of part of her tail. She fought with strength and grace every time.
              </p>
              
              <p>
                To give her the peaceful life she deserved, Jen and her family moved to Mission, settling on nearly four acres surrounded by mountains, sunshine, and creeks. It became Sadie's paradise.
              </p>
              
              <p>
                In July 2024, at 14½ years old, Sadie passed peacefully after her final battle with cancer. She gave her family 14 incredible years — and a new perspective on life: slow down, connect deeply, and love without limits.
              </p>
              
              <p className="text-foreground font-medium">
                Sadie's Mission Dog Boarding was created to honor her legacy — a safe, calm, loving place where dogs are understood, especially those who may not thrive in traditional boarding environments.
              </p>
              
              <p>
                Today, in the beautiful mountains of Stave Falls area in Mission, BC, Sadie's legacy continues.
              </p>
              
              <div className="pt-6 text-center">
                <p className="text-foreground font-medium">Here, dogs are not judged.</p>
                <p className="text-foreground font-medium">They are seen.</p>
                <p className="text-foreground font-medium">They are understood.</p>
                <p className="text-foreground font-medium">They belong.</p>
                <p className="text-xl font-bold text-primary mt-4 italic">And that is Sadie's Mission.</p>
              </div>
              
              {/* Sadie's Photo Album */}
              <div className="mt-12 pt-8 border-t border-border">
                <h3 className="text-xl font-bold text-foreground mb-4 text-center">
                  📸 Remembering Sadie
                </h3>
                <p className="text-center text-muted-foreground mb-6 text-sm">
                  Click any photo to view the album
                </p>
                <div className="grid grid-cols-4 md:grid-cols-7 lg:grid-cols-13 gap-3">
                  {sadiePhotos.map((photo, index) => (
                    <div 
                      key={index}
                      onClick={() => openLightbox(index)}
                      className="rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all cursor-pointer hover:scale-105"
                    >
                      <img
                        src={photo.url}
                        alt={photo.alt}
                        className="w-full h-24 object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="section-padding bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12 text-center">
              Our Mission & Values
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="text-center hover-lift">
                <CardContent className="pt-8 space-y-3">
                  <div className="text-4xl mb-4">❤️</div>
                  <h3 className="text-xl font-bold text-foreground">Compassionate Care</h3>
                  <p className="text-muted-foreground">
                    Every dog receives individualized attention and love, just as they would at home.
                  </p>
                </CardContent>
              </Card>
              <Card className="text-center hover-lift">
                <CardContent className="pt-8 space-y-3">
                  <div className="text-4xl mb-4">🛡️</div>
                  <h3 className="text-xl font-bold text-foreground">Safety First</h3>
                  <p className="text-muted-foreground">
                    We maintain the highest standards of safety and cleanliness in all our facilities.
                  </p>
                </CardContent>
              </Card>
              <Card className="text-center hover-lift">
                <CardContent className="pt-8 space-y-3">
                  <div className="text-4xl mb-4">🌟</div>
                  <h3 className="text-xl font-bold text-foreground">Excellence</h3>
                  <p className="text-muted-foreground">
                    We're committed to exceeding expectations in every aspect of pet care.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Meet Our Team */}
      <section id="team" className="section-padding bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">
              Meet Our Team
            </h2>
            <p className="text-lg text-muted-foreground mb-12 text-center max-w-3xl mx-auto">
              Our passionate team is dedicated to making every dog's stay comfortable, fun, and safe.
            </p>
            
            {/* Featured Owner Section - Jen */}
            {staffMembers.filter(s => s.id === 1).map((owner) => (
              <div key={owner.id} className="mb-16">
                <Card className="overflow-hidden">
                  <div className="grid md:grid-cols-3 gap-0">
                    <div className="md:col-span-1">
                      <img
                        src={owner.photoUrl}
                        alt={owner.name}
                        className="w-full h-full min-h-[400px] object-cover"
                      />
                    </div>
                    <div className="md:col-span-2 p-8 flex flex-col justify-center">
                      <h3 className="text-2xl font-bold text-foreground mb-1">{owner.name}</h3>
                      <p className="text-primary font-semibold mb-6">{owner.role}</p>
                      <div className="prose prose-sm text-muted-foreground space-y-4">
                        {owner.bio.split('\n\n').map((paragraph, idx) => (
                          <p key={idx} className={paragraph.includes("Because here") ? "font-semibold text-foreground italic" : ""}>
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            ))}

            {/* Featured Section - Envy */}
            {staffMembers.filter(s => s.id === 2).map((envy) => (
              <div key={envy.id} className="mb-16">
                <Card className="overflow-hidden">
                  <div className="grid md:grid-cols-3 gap-0">
                    <div className="md:col-span-1 md:order-2">
                      <img
                        src={envy.photoUrl}
                        alt={envy.name}
                        className="w-full h-full min-h-[400px] object-cover"
                      />
                    </div>
                    <div className="md:col-span-2 md:order-1 p-8 flex flex-col justify-center">
                      <h3 className="text-2xl font-bold text-foreground mb-1">{envy.name}</h3>
                      <p className="text-primary font-semibold mb-6">{envy.role}</p>
                      <div className="prose prose-sm text-muted-foreground space-y-4">
                        {envy.bio.split('\n\n').map((paragraph, idx) => (
                          <p key={idx} className={paragraph.includes("🐾") ? "font-semibold text-foreground" : ""}>
                            {paragraph}
                          </p>
                        ))}
                      </div>
                      
                      {/* Eric's Quote */}
                      {envy.dadQuote && (
                        <div className="mt-8 pt-8 border-t border-border">
                          <h4 className="text-lg font-bold text-foreground mb-4">From Envy's Dad:</h4>
                          <div className="bg-secondary/30 rounded-lg p-6 italic">
                            <div className="prose prose-sm text-muted-foreground space-y-4">
                              {envy.dadQuote.split('\n\n').map((paragraph, idx) => (
                                <p key={idx} className={paragraph.includes("— Eric") ? "font-semibold text-foreground not-italic mt-4" : ""}>
                                  {paragraph}
                                </p>
                              ))}
                            </div>
                          </div>
                          
                          {/* Photo of Envy with Jen below Eric's quote */}
                          {envy.photoUrl2 && (
                            <div className="mt-6 flex justify-center">
                              <img
                                src={envy.photoUrl2}
                                alt={`${envy.name} with Jen`}
                                className="w-64 h-auto rounded-lg shadow-lg object-cover"
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              </div>
            ))}
            
            {/* Other Team Members */}
            {staffMembers.filter(s => s.id !== 1 && s.id !== 2).length > 0 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {staffMembers.filter(s => s.id !== 1 && s.id !== 2).map((staff) => (
                  <Card key={staff.id} className="overflow-hidden hover-lift">
                    <div className="relative">
                      <img
                        src={staff.photoUrl}
                        alt={staff.name}
                        className="w-full h-72 object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                        <h3 className="text-xl font-bold text-white">{staff.name}</h3>
                        <p className="text-sm text-white/90">{staff.role}</p>
                      </div>
                    </div>
                    <CardContent className="p-6 space-y-4">
                      <p className="text-sm text-muted-foreground">{staff.bio}</p>
                      
                      {staff.certifications && staff.certifications.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-foreground mb-2">Certifications:</h4>
                          <div className="flex flex-wrap gap-2">
                            {staff.certifications.map((cert, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {cert}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div className="pt-2 border-t border-border">
                        <p className="text-xs font-medium text-primary">
                          <CheckCircle className="inline h-3 w-3 mr-1" />
                          Specialty: {staff.specialty}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
